/**
 * Two-factor (TOTP) settings card — multi-device.
 *
 * Everyone uses MFA. Employees + super-admins may register one authenticator;
 * tenant admins several (the card surfaces the cap). Flow: Add device → scan QR
 * → confirm a code → (on the first device) save recovery codes. Each device can
 * be removed with a current code (the "I changed my phone" reset path).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";
import { QRCodeSVG } from "qrcode.react";

import { api } from "@/lib/api";
import type { MfaAddDeviceResponse, MfaConfirmResponse, MfaStatus } from "@/lib/types";

export function MfaCard() {
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useQuery({
    queryKey: ["me-mfa"],
    queryFn: async () => (await api.get<MfaStatus>("/me/mfa")).data,
  });

  const [adding, setAdding] = useState<MfaAddDeviceResponse | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeCode, setRemoveCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const detail = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fallback;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["me-mfa"] });

  const addMut = useMutation({
    mutationFn: async () =>
      (await api.post<MfaAddDeviceResponse>("/me/mfa/devices", {})).data,
    onSuccess: (data) => {
      setError(null);
      setConfirmCode("");
      setAdding(data);
    },
    onError: (err) => setError(detail(err, "Could not start setup.")),
  });

  const confirmMut = useMutation({
    mutationFn: async () =>
      (
        await api.post<MfaConfirmResponse>(`/me/mfa/devices/${adding!.device_id}/confirm`, {
          code: confirmCode.trim(),
        })
      ).data,
    onSuccess: (data) => {
      setError(null);
      setAdding(null);
      setConfirmCode("");
      if (data.recovery_codes && data.recovery_codes.length > 0) {
        setRecoveryCodes(data.recovery_codes);
      }
      refresh();
    },
    onError: (err) => setError(detail(err, "That code didn't match. Try again.")),
  });

  const removeMut = useMutation({
    mutationFn: async (deviceId: string) =>
      api.delete(`/me/mfa/devices/${deviceId}`, { data: { code: removeCode.trim() } }),
    onSuccess: () => {
      setError(null);
      setRemovingId(null);
      setRemoveCode("");
      refresh();
    },
    onError: (err) => setError(detail(err, "Could not remove. Check the code and try again.")),
  });

  const devices = status?.devices ?? [];
  const confirmed = devices.filter((d) => d.confirmed);
  const maxDevices = status?.max_devices ?? 1;
  const canAdd = !adding && confirmed.length < maxDevices;

  return (
    <section style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Two-factor authentication</h2>
        {status?.enabled && <span style={enabledBadge}>Enabled</span>}
      </div>
      <p style={{ fontSize: 13, color: "var(--body-mid)", margin: "0 0 16px" }}>
        Add a one-time code from an authenticator app (Google Authenticator, Microsoft
        Authenticator, Authy) on top of your password.
        {maxDevices > 1
          ? ` You can register up to ${maxDevices} devices.`
          : " One device per account."}
      </p>

      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}

      {isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : recoveryCodes ? (
        <RecoveryCodes codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
      ) : (
        <>
          {/* Registered devices */}
          {confirmed.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              {confirmed.map((d) => (
                <div key={d.id} style={deviceRow}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>
                      {d.label}
                    </div>
                    {d.created_at && (
                      <div style={{ fontSize: 11, color: "var(--mute)" }}>
                        Added {new Date(d.created_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  {removingId === d.id ? (
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        inputMode="text"
                        placeholder="current code"
                        value={removeCode}
                        onChange={(e) => setRemoveCode(e.target.value)}
                        style={{ ...input, width: 130, fontFamily: "var(--font-mono)" }}
                      />
                      <button
                        type="button"
                        onClick={() => removeMut.mutate(d.id)}
                        disabled={removeMut.isPending || !removeCode.trim()}
                        style={{ ...btnGhost, color: "#ee1d36", borderColor: "rgba(238,29,54,0.3)" }}
                      >
                        {removeMut.isPending ? "Removing…" : "Confirm"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRemovingId(null);
                          setRemoveCode("");
                        }}
                        style={btnGhost}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setRemovingId(d.id);
                      }}
                      style={btnGhost}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add-device flow */}
          {adding ? (
            <div>
              <p style={{ fontSize: 13, color: "var(--body)", margin: "0 0 12px" }}>
                Scan this with your authenticator app, then enter the 6-digit code to finish.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 20,
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    padding: 10,
                    border: "1px solid var(--hairline)",
                    borderRadius: 6,
                  }}
                >
                  <QRCodeSVG value={adding.otpauth_uri} size={148} />
                </div>
                <div>
                  <span style={fieldLabel}>Or enter this key manually</span>
                  <code style={secretBox}>{adding.secret}</code>
                </div>
              </div>
              <label style={{ display: "block", marginBottom: 12 }}>
                <span style={fieldLabel}>6-digit code</span>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value)}
                  style={{ ...input, maxWidth: 160, letterSpacing: "0.3em", fontFamily: "var(--font-mono)" }}
                />
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => confirmMut.mutate()}
                  disabled={confirmMut.isPending || !confirmCode.trim()}
                  style={{ ...btnPrimary, opacity: confirmMut.isPending || !confirmCode.trim() ? 0.6 : 1 }}
                >
                  {confirmMut.isPending ? "Verifying…" : "Verify and add"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAdding(null);
                    setConfirmCode("");
                    setError(null);
                  }}
                  style={btnGhost}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : canAdd ? (
            <button type="button" onClick={() => addMut.mutate()} disabled={addMut.isPending} style={btnPrimary}>
              {addMut.isPending
                ? "Starting…"
                : confirmed.length === 0
                  ? "Set up authenticator app"
                  : "Add another device"}
            </button>
          ) : (
            <p style={{ fontSize: 12.5, color: "var(--mute)", margin: 0 }}>
              You've reached the maximum of {maxDevices} device{maxDevices === 1 ? "" : "s"}. Remove
              one to add a new one.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div>
      <p style={{ fontSize: 13, color: "var(--body)", margin: "0 0 4px", fontWeight: 600 }}>
        Save your recovery codes
      </p>
      <p style={{ fontSize: 13, color: "var(--body-mid)", margin: "0 0 12px" }}>
        Store these somewhere safe. Each one signs you in once if you lose your phone. They won't be
        shown again.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 8,
          padding: 14,
          background: "var(--surface-1)",
          border: "1px solid var(--hairline)",
          borderRadius: 6,
          marginBottom: 14,
        }}
      >
        {codes.map((c) => (
          <code key={c} style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink)" }}>
            {c}
          </code>
        ))}
      </div>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(codes.join("\n"))?.catch(() => {});
          onDone();
        }}
        style={btnPrimary}
      >
        Copy and finish
      </button>
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
  marginBottom: 16,
};

const deviceRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "10px 12px",
  border: "1px solid var(--hairline)",
  borderRadius: 6,
  marginBottom: 8,
};

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const secretBox: CSSProperties = {
  display: "inline-block",
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  letterSpacing: "0.05em",
  background: "var(--surface-2)",
  padding: "6px 10px",
  borderRadius: 4,
  wordBreak: "break-all",
  color: "var(--ink)",
};

const enabledBadge: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  padding: "2px 8px",
  background: "rgba(0,215,34,0.12)",
  color: "#047a18",
  borderRadius: 3,
};

const alertStyle: CSSProperties = {
  marginBottom: 12,
  padding: "8px 12px",
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
};

const btnPrimary: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

const btnGhost: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: 12.5,
  color: "var(--ink)",
  cursor: "pointer",
};
